<template>
  <callout
    :border-color="statusLayout.borderColor"
    class="mb-3"
    :controlled="true"
    has-gradient
    :has-toggle="true"
    :opened="isCalloutOpen"
    @click:open="toggleHistorySection"
  >
    <div class="row info-container">
      <div class="col-12 col-md-2 info-container__balloon">
        <custom-balloon
          :background-color="statusLayout.color"
          class="w-100 mt-3 mt-md-0 mx-0"
          :text="document.proposalDocumentStatusName"
        />
      </div>
      <div class="col-12 col-md-10">
        <document-info
          :add-file="canBeSubmitted"
          :can-replace-denied="statusLayout.isDenied"
          :can-replace-requested="statusLayout.isRequestReplacement"
          :can-request-replacement-approved="canRequestReplacement"
          :document="document"
          document-title="Tipo de Documento:"
          :file-preview="canShowFilePreview && !canEditDocuments"
          :show-action-btn="canManageDocument"
          show-filename
          @manageDocumentStatus="manageDocumentStatus"
          @on-upload-file="onUploadFile"
          @request-replacement="openRequestReplacement"
        />
      </div>
    </div>
    <div slot="edition">
      <div class="bg-secondary-3 p-2">
        <div class="bg-white rounded p-4">
          <ContractDocumentStatusHistory
            v-if="showStatusHisotry"
            :history="statusHistory"
            @fetch-history="fetchHistory"
          />
          <div v-else-if="showDenyForm" class="row">
            <div class="col-12 mb-3">
              <em class="fas fa-bell-exclamation text-orange mr-1" />
              <span class="color-gray-7 font-weight-bold font-16">
                Raz&oacute;n para Denegar
              </span>
            </div>
            <div class="col-12 col-md-7 mb-3 mb-md-0">
              <validation-observer ref="proposalContractDocuments">
                <validation-provider
                  v-slot="{ errors }"
                  name="Archivo"
                  rules="required"
                >
                  <custom-input
                    v-model="razonByDenyDocument"
                    custom-error-msg="Este campo es requerido para continuar con el proceso."
                    :error="errors[0]"
                    label="Razón:"
                    maxlength="3000"
                    type="text"
                  />
                </validation-provider>
              </validation-observer>
            </div>
            <div
              class="col-12 col-md-5 d-flex justify-content-end align-items-end"
            >
              <action-button
                id="modal-btn-cancelar"
                i="fas fa-times-circle"
                variant="outline-danger mr-2"
                @click="isCalloutOpen = false"
              >
                Cancelar
              </action-button>

              <action-button
                i="fa fa-save"
                variant="success px-3"
                @click="denyContractDocument()"
              >
                Guardar
              </action-button>
            </div>
          </div>
          <div v-else-if="showRequestReplacementForm">
            <ContractDocumentReplacement
              @cancel="closeRequestReplacement"
              @confirm="handleConfirmReplacement"
            />
          </div>
        </div>
      </div>
    </div>
  </callout>
</template>

<script>
import Callout from 'custom-callout';
import DocumentInfo from '@/views/provider/proposalHistory/components/DocumentInfo.vue';
import proposalDocumentStatus from '@/views/provider/proposalHistory/components/proposalDocumentStatus.js';
import {
  resolveColor,
  resolveColorHexa,
} from '@/views/provider/proposalHistory/components/proposalDocumentStatus.js';
import CustomBalloon from 'custom-balloon';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import {
  denyContractDocument,
  approveContractDocument,
  uploadContractDocument,
  getContractDocumentHistory,
  requestReplacementDocument,
} from '@/modules/serviceProposalHistory/api';
import alertMixin from 'utils/mixins/alert.mixin.js';
import { serialize as objectToFormData } from 'object-to-formdata';
import documentTypes from '@/utils/shared/constants/documentTypes';
import ContractDocumentStatusHistory from './ContractDocumentStatusHistory.vue';
import ContractDocumentReplacement from './ContractDocumentReplacement.vue';

export default {
  name: 'ContractDocumentContainer',
  components: {
    Callout,
    DocumentInfo,
    CustomBalloon,
    ActionButton,
    CustomInput,
    ContractDocumentStatusHistory,
    ContractDocumentReplacement,
  },
  mixins: [alertMixin],
  props: {
    contractDocument: {
      type: Object,
      default: () => {},
      required: true,
    },
    showSubmit: {
      type: Boolean,
      default: () => false,
    },
    proposalRequestId: {
      type: Number,
      required: true,
      default: () => 0,
    },
  },
  data: () => ({
    document: {},
    isCalloutOpen: false,
    razonByDenyDocument: null,
    showDenyForm: false,
    showStatusHisotry: false,
    showRequestReplacementForm: false,
    statusHistory: [],
  }),
  computed: {
    isAdmin() {
      return this.userHasPermissions('proposal.administrator');
    },
    isProvider() {
      return this.userHasPermissions('proposal.provider');
    },
    canEditDocuments() {
      return this.userHasPermissions('proposal.changedocuments');
    },
    canRequestReplacement() {
      return (
        this.userHasPermissions('proposal.providerDocuments.replace') &&
        this.statusLayout.isApproved
      );
    },
    hasPermissions() {
      return this.isProvider || this.isAdmin;
    },
    canManageDocument() {
      return this.isAdmin && this.statusLayout.isSubmitted;
    },
    canBeSubmitted() {
      return this.showSubmit && this.statusLayout.isPedding;
    },
    canShowFilePreview() {
      return (
        (this.hasPermissions &&
          !this.showSubmit &&
          this.statusLayout.isDenied) ||
        (this.hasPermissions && this.statusLayout.isApproved) ||
        (this.hasPermissions && this.statusLayout.isDenied) ||
        (this.hasPermissions && this.statusLayout.isRequestReplacement) ||
        (this.isProvider && this.statusLayout.isSubmitted)
      );
    },
    statusLayout() {
      switch (this.document.proposalDocumentStatusId) {
        case proposalDocumentStatus.Approved:
          return {
            color: resolveColor(proposalDocumentStatus.Approved),
            borderColor: resolveColorHexa(proposalDocumentStatus.Approved),
            isApproved: true,
            isDenied: false,
            isPedding: false,
            isSubmitted: false,
            isRequestReplacement: false,
          };
        case proposalDocumentStatus.Denied:
          return {
            color: resolveColor(proposalDocumentStatus.Denied),
            borderColor: resolveColorHexa(proposalDocumentStatus.Denied),
            isDenied: true,
            isApproved: false,
            isPedding: false,
            isSubmitted: false,
            isRequestReplacement: false,
          };
        case proposalDocumentStatus.Pending:
          return {
            color: resolveColor(proposalDocumentStatus.Pending),
            borderColor: resolveColorHexa(proposalDocumentStatus.Pending),
            isPedding: true,
            isSubmitted: false,
            isApproved: false,
            isDenied: false,
            isRequestReplacement: false,
          };
        case proposalDocumentStatus.Submitted:
          return {
            color: resolveColor(proposalDocumentStatus.Submitted),
            borderColor: resolveColorHexa(proposalDocumentStatus.Submitted),
            isSubmitted: true,
            isApproved: false,
            isDenied: false,
            isPedding: false,
            isRequestReplacement: false,
          };
        case proposalDocumentStatus.RequestReplacement:
          return {
            color: resolveColor(proposalDocumentStatus.RequestReplacement),
            borderColor: resolveColorHexa(
              proposalDocumentStatus.RequestReplacement
            ),
            isSubmitted: false,
            isApproved: false,
            isDenied: false,
            isPedding: false,
            isRequestReplacement: true,
          };
        default: {
          return null;
        }
      }
    },
  },
  watch: {
    'contractDocument.proposalDocumentStatusId'() {
      this.isCalloutOpen = false;
    },
    contractDocument: {
      deep: true,
      immediate: true,
      handler(current) {
        const document = {
          ...current,
          documentTypeName: current.documentDescription,
          documentId: current.documentAttachmentId,
        };

        this.document = document;
        delete this.document.documentAttachmentId;
        delete this.document.documentDescription;
      },
    },
  },
  methods: {
    openRequestReplacement() {
      this.isCalloutOpen = true;
      this.showRequestReplacementForm = true;
      this.showDenyForm = false;
      this.showStatusHisotry = false;
    },
    closeRequestReplacement() {
      this.isCalloutOpen = false;
      this.showRequestReplacementForm = false;
    },
    async handleConfirmReplacement(comment) {
      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea solicitar reemplazo?',
        'Si',
        'No'
      );

      if (!answer) {
        return;
      }

      try {
        const parameters = {
          proposalRequestId: this.proposalRequestId,
          documentAttachmentId: this.document.documentId,
          comment: comment,
        };
        await requestReplacementDocument(parameters);

        this.responseHandler('solicitado a reemplazo');
        this.closeRequestReplacement();
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err?.response?.data?.message ?? 'error',
          'error',
          5000
        );
      }
    },
    async onUploadFile(file) {
      const params = objectToFormData(
        {
          proposalRequestId: this.proposalRequestId,
          proposalDocumentTypeId: this.document.proposalDocumentTypeId,
          documentId: this.document.documentId,
          DocumentTypeId: documentTypes.ContractDocument,
          file,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      try {
        await uploadContractDocument(params);
        this.$emit('on-file-upload');

        this.ShowToast(
          'Alerta',
          'El archivo fue guardado exitosamente',
          'success'
        );

        if (this.statusLayout.isDenied) {
          this.isCalloutOpen = false;
          this.$emit('on-status-change');
        }
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err.response?.data?.message ?? 'Error',
          'error',
          5000
        );
      }
    },
    responseHandler(action) {
      this.$emit('on-status-change');
      this.ShowToast(
        'Alerta',
        `El documento ha sido ${action} exitosamente.`,
        'success',
        5000
      );
    },
    openDenySection() {
      this.showDenyForm = true;
      this.isCalloutOpen = true;
      this.showStatusHisotry = false;
      this.showRequestReplacementForm = false;
    },
    toggleHistorySection() {
      this.isCalloutOpen = !this.isCalloutOpen;

      if (!this.isCalloutOpen) {
        return;
      }

      this.showDenyForm = false;
      this.showRequestReplacementForm = false;
      this.showStatusHisotry = true;
    },
    async manageDocumentStatus(isApproved) {
      if (!isApproved) {
        this.openDenySection();
        return;
      }

      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea Aprobar el documento?',
        'Si',
        'No'
      );

      if (!answer) {
        return;
      }

      const parameters = {
        proposalRequestId: this.proposalRequestId,
        documentAttachmentId: this.document.documentId,
      };

      try {
        await approveContractDocument(parameters);
        this.responseHandler('aprobado');
        this.isCalloutOpen = false;
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err?.response?.data?.message ?? 'error',
          'error',
          5000
        );
      }
    },
    async denyContractDocument() {
      const isValid = await this.$refs.proposalContractDocuments.validate();
      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error', 5000);
        return;
      }

      const answer = await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea Denegar el documento?',
        'Si',
        'No'
      );

      if (!answer) {
        return;
      }

      const parameters = {
        proposalRequestId: this.proposalRequestId,
        documentAttachmentId: this.document.documentId,
        RazonDeny: this.razonByDenyDocument,
      };

      try {
        await denyContractDocument(parameters);
        this.responseHandler('denegado');
        this.isCalloutOpen = false;
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err?.response?.data?.message ?? 'error',
          'error',
          5000
        );
      }
    },
    async fetchHistory() {
      const { data } = await getContractDocumentHistory(
        this.proposalRequestId,
        this.document.proposalDocumentTypeId
      );

      this.statusHistory = [...data];
    },
  },
};
</script>

<style lang="scss" scoped>
.info-container {
  display: flex;
  align-items: center;
  &__balloon::v-deep {
    @extend .info-container;
    justify-content: center;
    border-right: 2px solid #acb7b1;
    height: 70px;
    .balloon-container {
      font-size: 1rem;
      font-weight: bold;
    }
  }
}
</style>
